import React, { useState, useEffect, useRef } from 'react';
import {
  getShippingBoxes,
  getShippingBox,
  createShippingBox,
  getAvailableProductsForShipping,
  getShippingPDF,
  getCarriers,
  createCarrier,
  getBoxSizes,
  createBoxSize,
  updateShippingDate,
  updateShippingCost,
  updateShippingBox,
  addShippingItem,
  updateShippingItem,
  removeShippingItem,
  getNextBoxNumber,
} from '../api';

function Shipping({ user }) {
  const [boxes, setBoxes] = useState([]);
  const [products, setProducts] = useState([]);
  const [carriers, setCarriers] = useState([]);
  const [boxSizes, setBoxSizes] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [showAddCarrier, setShowAddCarrier] = useState(false);
  const [newCarrierName, setNewCarrierName] = useState('');
  const [showAddBoxSize, setShowAddBoxSize] = useState(false);
  const [newBoxSizeName, setNewBoxSizeName] = useState('');
  const [formData, setFormData] = useState({
    box_number: '',
    carrier: 'LBC Express',
    tracking_number: '',
    box_size: 'Extra Large (24x18x24)',
    shipping_cost: '',
  });
  const [items, setItems] = useState([{ product_id: '', quantity: 1, non_trackable_notes: '' }]);
  const [editingShippingDate, setEditingShippingDate] = useState({ boxId: null, date: '' });
  const [viewingBox, setViewingBox] = useState(null);
  const [editingBox, setEditingBox] = useState(false);
  const [editBoxForm, setEditBoxForm] = useState({
    carrier: '',
    tracking_number: '',
    box_size: '',
    shipping_cost: '',
  });
  const [newItem, setNewItem] = useState({ product_id: '', quantity: 1, non_trackable_notes: '' });
  const [showAddItem, setShowAddItem] = useState(false);
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');

  // Product search state
  const [productSearches, setProductSearches] = useState({});
  const [showProductDropdowns, setShowProductDropdowns] = useState({});
  const [newItemSearch, setNewItemSearch] = useState('');
  const [showNewItemDropdown, setShowNewItemDropdown] = useState(false);

  // Close dropdowns when clicking outside
  useEffect(() => {
    const handleClickOutside = () => {
      setShowProductDropdowns({});
      setShowNewItemDropdown(false);
    };
    document.addEventListener('click', handleClickOutside);
    return () => document.removeEventListener('click', handleClickOutside);
  }, []);

  useEffect(() => {
    loadBoxes();
    loadProducts();
    loadCarriers();
    loadBoxSizes();
  }, []);

  const loadBoxes = async () => {
    try {
      const response = await getShippingBoxes();
      setBoxes(response.data);
    } catch (err) {
      setError('Failed to load shipping boxes');
    }
  };

  const loadProducts = async () => {
    try {
      const response = await getAvailableProductsForShipping();
      setProducts(response.data);
    } catch (err) {
      console.error('Failed to load products');
    }
  };

  // Filter products based on search term
  const getFilteredProducts = (searchTerm) => {
    if (!searchTerm) return products;
    const search = searchTerm.toLowerCase();
    return products.filter(p =>
      p.name.toLowerCase().includes(search) ||
      (p.description && p.description.toLowerCase().includes(search))
    );
  };

  // Get product name by ID
  const getProductName = (productId) => {
    const product = products.find(p => p.id.toString() === productId.toString());
    return product ? `${product.name} (${product.available} available)` : '';
  };

  const loadCarriers = async () => {
    try {
      const response = await getCarriers();
      setCarriers(response.data);
    } catch (err) {
      console.error('Failed to load carriers');
    }
  };

  const handleAddCarrier = async () => {
    if (!newCarrierName.trim()) return;

    try {
      const response = await createCarrier(newCarrierName.trim());
      setCarriers([...carriers, response.data]);
      setFormData({ ...formData, carrier: response.data.name });
      setNewCarrierName('');
      setShowAddCarrier(false);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to add carrier');
    }
  };

  const loadBoxSizes = async () => {
    try {
      const response = await getBoxSizes();
      setBoxSizes(response.data);
    } catch (err) {
      console.error('Failed to load box sizes');
    }
  };

  const handleAddBoxSize = async () => {
    if (!newBoxSizeName.trim()) return;

    try {
      const response = await createBoxSize(newBoxSizeName.trim());
      setBoxSizes([...boxSizes, response.data]);
      setFormData({ ...formData, box_size: response.data.name });
      setNewBoxSizeName('');
      setShowAddBoxSize(false);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to add box size');
    }
  };

  const handleSaveShippingDate = async (boxId) => {
    try {
      await updateShippingDate(boxId, editingShippingDate.date);
      setMessage('Shipping date updated successfully');
      setEditingShippingDate({ boxId: null, date: '' });
      loadBoxes();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to update shipping date');
    }
  };

  const loadNextBoxNumber = async () => {
    try {
      const response = await getNextBoxNumber();
      setFormData(prev => ({ ...prev, box_number: response.data.next_box_number }));
    } catch (err) {
      console.error('Failed to load next box number');
    }
  };

  const handleShowForm = () => {
    setShowForm(true);
    loadNextBoxNumber();
  };

  const viewBox = async (boxId) => {
    try {
      const response = await getShippingBox(boxId);
      setViewingBox(response.data);
      setEditingBox(false);
      setShowAddItem(false);
      setEditBoxForm({
        carrier: response.data.carrier || '',
        tracking_number: response.data.tracking_number || '',
        box_size: response.data.box_size || '',
        shipping_cost: response.data.shipping_cost?.toString() || '',
      });
    } catch (err) {
      setError('Failed to load box details');
    }
  };

  const handleEditBox = () => {
    setEditingBox(true);
    setError('');
    setMessage('');
  };

  const handleSaveBoxEdit = async () => {
    try {
      // Update box details
      await updateShippingBox(viewingBox.id, {
        carrier: editBoxForm.carrier,
        tracking_number: editBoxForm.tracking_number,
        box_size: editBoxForm.box_size,
      });

      // Update shipping cost separately if changed
      if (editBoxForm.shipping_cost !== (viewingBox.shipping_cost?.toString() || '')) {
        await updateShippingCost(viewingBox.id, editBoxForm.shipping_cost);
      }

      setMessage('Shipping box updated successfully');
      // Reload the box
      const response = await getShippingBox(viewingBox.id);
      setViewingBox(response.data);
      setEditingBox(false);
      loadBoxes();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to update shipping box');
    }
  };

  const handleAddItemToBox = async () => {
    if (!newItem.quantity || newItem.quantity < 1) {
      setError('Quantity must be at least 1');
      return;
    }

    try {
      await addShippingItem(viewingBox.id, {
        product_id: newItem.product_id || null,
        quantity: parseInt(newItem.quantity),
        non_trackable_notes: newItem.non_trackable_notes,
      });

      setMessage('Item added successfully');
      // Reload the box
      const response = await getShippingBox(viewingBox.id);
      setViewingBox(response.data);
      setNewItem({ product_id: '', quantity: 1, non_trackable_notes: '' });
      setShowAddItem(false);
      loadProducts(); // Refresh available inventory
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to add item');
    }
  };

  const handleUpdateItemQuantity = async (itemId, quantity) => {
    try {
      await updateShippingItem(viewingBox.id, itemId, parseInt(quantity));
      // Reload the box
      const response = await getShippingBox(viewingBox.id);
      setViewingBox(response.data);
      loadProducts();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to update quantity');
    }
  };

  const handleRemoveItem = async (itemId) => {
    if (!window.confirm('Are you sure you want to remove this item?')) return;

    try {
      await removeShippingItem(viewingBox.id, itemId);
      setMessage('Item removed successfully');
      // Reload the box
      const response = await getShippingBox(viewingBox.id);
      setViewingBox(response.data);
      loadProducts();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to remove item');
    }
  };

  const isBoxEditable = (box) => !box.shipping_date;

  const addItem = () => {
    setItems([...items, { product_id: '', quantity: 1, non_trackable_notes: '' }]);
  };

  const removeItem = (index) => {
    setItems(items.filter((_, i) => i !== index));
  };

  const updateItem = (index, field, value) => {
    const newItems = [...items];
    newItems[index][field] = value;
    setItems(newItems);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setMessage('');

    try {
      await createShippingBox({
        ...formData,
        items: items.map(item => ({
          ...item,
          product_id: item.product_id || null,
          quantity: parseInt(item.quantity),
        })),
      });

      setMessage('Shipping box created successfully!');
      setFormData({ box_number: '', carrier: 'LBC Express', tracking_number: '', box_size: 'Extra Large (24x18x24)', shipping_cost: '' });
      setItems([{ product_id: '', quantity: 1, non_trackable_notes: '' }]);
      setProductSearches({});
      setShowProductDropdowns({});
      setShowForm(false);
      loadBoxes();
      loadProducts();
      loadNextBoxNumber();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to create shipping box');
    }
  };

  const downloadPDF = async (boxId, boxNumber) => {
    try {
      const response = await getShippingPDF(boxId);
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `shipping-list-${boxNumber}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (err) {
      setError('Failed to download PDF');
    }
  };

  if (user.role !== 'admin') {
    return (
      <div className="container">
        <div className="alert alert-error">
          Only administrators can create shipping lists.
        </div>
      </div>
    );
  }

  return (
    <div className="container">
      <div className="card">
        <div className="card-header">Shipping Management</div>

        {message && <div className="alert alert-success">{message}</div>}
        {error && <div className="alert alert-error">{error}</div>}

        {!showForm ? (
          <>
            <button
              className="btn btn-primary"
              onClick={handleShowForm}
              style={{ marginBottom: '20px' }}
            >
              Create New Shipping Box
            </button>

            <div className="table-container">
              <table>
                <thead>
                  <tr>
                    <th>Box Number</th>
                    <th>Carrier</th>
                    <th>Tracking</th>
                    <th>Box Size</th>
                    <th>Shipping Cost</th>
                    <th>Created</th>
                    <th>Shipping Date</th>
                    <th>Status</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {boxes.map((box) => (
                    <tr key={box.id}>
                      <td>{box.box_number}</td>
                      <td>{box.carrier || '-'}</td>
                      <td>{box.tracking_number || '-'}</td>
                      <td>{box.box_size || '-'}</td>
                      <td>{box.shipping_cost ? `$${parseFloat(box.shipping_cost).toFixed(2)}` : '-'}</td>
                      <td>{new Date(box.created_at).toLocaleDateString()}</td>
                      <td>
                        {editingShippingDate.boxId === box.id ? (
                          <div style={{ display: 'flex', gap: '5px', alignItems: 'center' }}>
                            <input
                              type="date"
                              value={editingShippingDate.date}
                              onChange={(e) => setEditingShippingDate({ ...editingShippingDate, date: e.target.value })}
                              style={{ width: '130px' }}
                            />
                            <button
                              className="btn btn-success btn-sm"
                              onClick={() => handleSaveShippingDate(box.id)}
                              style={{ padding: '4px 8px' }}
                            >
                              Save
                            </button>
                            <button
                              className="btn btn-secondary btn-sm"
                              onClick={() => setEditingShippingDate({ boxId: null, date: '' })}
                              style={{ padding: '4px 8px' }}
                            >
                              Cancel
                            </button>
                          </div>
                        ) : (
                          <span
                            style={{ cursor: 'pointer', textDecoration: 'underline' }}
                            onClick={() => setEditingShippingDate({ boxId: box.id, date: box.shipping_date || '' })}
                          >
                            {box.shipping_date ? new Date(box.shipping_date).toLocaleDateString() : 'Set date'}
                          </span>
                        )}
                      </td>
                      <td>
                        <span className={`badge badge-${box.status}`}>
                          {box.status}
                        </span>
                      </td>
                      <td>
                        <div style={{ display: 'flex', gap: '5px' }}>
                          <button
                            className="btn btn-primary btn-sm"
                            onClick={() => viewBox(box.id)}
                            style={{ padding: '6px 12px', fontSize: '0.9rem' }}
                          >
                            View
                          </button>
                          <button
                            className="btn btn-secondary btn-sm"
                            onClick={() => downloadPDF(box.id, box.box_number)}
                            style={{ padding: '6px 12px', fontSize: '0.9rem' }}
                          >
                            PDF
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </>
        ) : (
          <form onSubmit={handleSubmit}>
            <div className="form-group">
              <label>Box Number *</label>
              <input
                type="text"
                value={formData.box_number}
                onChange={(e) =>
                  setFormData({ ...formData, box_number: e.target.value })
                }
                required
              />
            </div>

            <div className="form-group">
              <label>Carrier</label>
              {!showAddCarrier ? (
                <div style={{ display: 'flex', gap: '10px' }}>
                  <select
                    value={formData.carrier}
                    onChange={(e) =>
                      setFormData({ ...formData, carrier: e.target.value })
                    }
                    style={{ flex: 1 }}
                  >
                    {carriers.map((carrier) => (
                      <option key={carrier.id} value={carrier.name}>
                        {carrier.name}
                      </option>
                    ))}
                  </select>
                  <button
                    type="button"
                    className="btn btn-secondary"
                    onClick={() => setShowAddCarrier(true)}
                    style={{ padding: '8px 12px' }}
                  >
                    + New
                  </button>
                </div>
              ) : (
                <div style={{ display: 'flex', gap: '10px' }}>
                  <input
                    type="text"
                    value={newCarrierName}
                    onChange={(e) => setNewCarrierName(e.target.value)}
                    placeholder="Enter new carrier name"
                    style={{ flex: 1 }}
                  />
                  <button
                    type="button"
                    className="btn btn-success"
                    onClick={handleAddCarrier}
                    style={{ padding: '8px 12px' }}
                  >
                    Add
                  </button>
                  <button
                    type="button"
                    className="btn btn-secondary"
                    onClick={() => {
                      setShowAddCarrier(false);
                      setNewCarrierName('');
                    }}
                    style={{ padding: '8px 12px' }}
                  >
                    Cancel
                  </button>
                </div>
              )}
            </div>

            <div className="form-group">
              <label>Tracking Number</label>
              <input
                type="text"
                value={formData.tracking_number}
                onChange={(e) =>
                  setFormData({ ...formData, tracking_number: e.target.value })
                }
              />
            </div>

            <div className="form-group">
              <label>Box Size</label>
              {!showAddBoxSize ? (
                <div style={{ display: 'flex', gap: '10px' }}>
                  <select
                    value={formData.box_size}
                    onChange={(e) =>
                      setFormData({ ...formData, box_size: e.target.value })
                    }
                    style={{ flex: 1 }}
                  >
                    {boxSizes.map((size) => (
                      <option key={size.id} value={size.name}>
                        {size.name}
                      </option>
                    ))}
                  </select>
                  <button
                    type="button"
                    className="btn btn-secondary"
                    onClick={() => setShowAddBoxSize(true)}
                    style={{ padding: '8px 12px' }}
                  >
                    + New
                  </button>
                </div>
              ) : (
                <div style={{ display: 'flex', gap: '10px' }}>
                  <input
                    type="text"
                    value={newBoxSizeName}
                    onChange={(e) => setNewBoxSizeName(e.target.value)}
                    placeholder="Enter new box size"
                    style={{ flex: 1 }}
                  />
                  <button
                    type="button"
                    className="btn btn-success"
                    onClick={handleAddBoxSize}
                    style={{ padding: '8px 12px' }}
                  >
                    Add
                  </button>
                  <button
                    type="button"
                    className="btn btn-secondary"
                    onClick={() => {
                      setShowAddBoxSize(false);
                      setNewBoxSizeName('');
                    }}
                    style={{ padding: '8px 12px' }}
                  >
                    Cancel
                  </button>
                </div>
              )}
            </div>

            <div className="form-group">
              <label>Shipping Cost</label>
              <input
                type="number"
                step="0.01"
                value={formData.shipping_cost}
                onChange={(e) =>
                  setFormData({ ...formData, shipping_cost: e.target.value })
                }
              />
            </div>

            <h3 style={{ marginTop: '20px', marginBottom: '15px' }}>Items</h3>

            {items.map((item, index) => (
              <div
                key={index}
                style={{
                  border: '1px solid #ddd',
                  padding: '15px',
                  borderRadius: '5px',
                  marginBottom: '15px',
                }}
              >
                <div className="form-group">
                  <label>Product (Optional for non-trackable items)</label>
                  <div style={{ position: 'relative' }} onClick={(e) => e.stopPropagation()}>
                    <input
                      type="text"
                      value={item.product_id ? getProductName(item.product_id) : (productSearches[index] || '')}
                      onChange={(e) => {
                        setProductSearches({ ...productSearches, [index]: e.target.value });
                        setShowProductDropdowns({ ...showProductDropdowns, [index]: true });
                        if (item.product_id) {
                          updateItem(index, 'product_id', '');
                        }
                      }}
                      onFocus={() => setShowProductDropdowns({ ...showProductDropdowns, [index]: true })}
                      placeholder="Type to search products..."
                    />
                    {item.product_id && (
                      <button
                        type="button"
                        onClick={() => {
                          updateItem(index, 'product_id', '');
                          setProductSearches({ ...productSearches, [index]: '' });
                        }}
                        style={{
                          position: 'absolute',
                          right: '10px',
                          top: '50%',
                          transform: 'translateY(-50%)',
                          background: 'none',
                          border: 'none',
                          fontSize: '1.2rem',
                          cursor: 'pointer',
                          color: 'var(--color-text-muted)'
                        }}
                      >
                        ✕
                      </button>
                    )}
                    {showProductDropdowns[index] && !item.product_id && (
                      <div style={{
                        position: 'absolute',
                        top: '100%',
                        left: 0,
                        right: 0,
                        maxHeight: '200px',
                        overflowY: 'auto',
                        backgroundColor: 'white',
                        border: '1px solid #ddd',
                        borderRadius: '4px',
                        zIndex: 100,
                        boxShadow: '0 4px 6px rgba(0,0,0,0.1)'
                      }}>
                        {getFilteredProducts(productSearches[index] || '').length === 0 ? (
                          <div style={{ padding: '10px', color: 'var(--color-text-muted)' }}>
                            No products with available inventory
                          </div>
                        ) : (
                          getFilteredProducts(productSearches[index] || '').map(product => (
                            <div
                              key={product.id}
                              onClick={() => {
                                updateItem(index, 'product_id', product.id.toString());
                                setProductSearches({ ...productSearches, [index]: '' });
                                setShowProductDropdowns({ ...showProductDropdowns, [index]: false });
                              }}
                              style={{
                                padding: '10px',
                                cursor: 'pointer',
                                borderBottom: '1px solid #eee',
                                display: 'flex',
                                justifyContent: 'space-between'
                              }}
                              onMouseEnter={(e) => e.target.style.backgroundColor = '#f5f5f5'}
                              onMouseLeave={(e) => e.target.style.backgroundColor = 'white'}
                            >
                              <span>{product.name}</span>
                              <span style={{ color: 'var(--color-success)', fontWeight: 'bold' }}>
                                {product.available} avail
                              </span>
                            </div>
                          ))
                        )}
                      </div>
                    )}
                  </div>
                </div>

                <div className="form-group">
                  <label>Quantity *</label>
                  <input
                    type="number"
                    value={item.quantity}
                    onChange={(e) =>
                      updateItem(index, 'quantity', e.target.value)
                    }
                    min="1"
                    required
                  />
                </div>

                <div className="form-group">
                  <label>Non-trackable Item Notes</label>
                  <textarea
                    value={item.non_trackable_notes}
                    onChange={(e) =>
                      updateItem(index, 'non_trackable_notes', e.target.value)
                    }
                    placeholder="For items not in inventory"
                  />
                </div>

                {items.length > 1 && (
                  <button
                    type="button"
                    className="btn btn-danger"
                    onClick={() => removeItem(index)}
                  >
                    Remove Item
                  </button>
                )}
              </div>
            ))}

            <button
              type="button"
              className="btn btn-secondary"
              onClick={addItem}
              style={{ marginBottom: '20px' }}
            >
              Add Another Item
            </button>

            <div className="btn-group">
              <button type="submit" className="btn btn-success">
                Create Shipping Box
              </button>
              <button
                type="button"
                className="btn btn-secondary"
                onClick={() => setShowForm(false)}
              >
                Cancel
              </button>
            </div>
          </form>
        )}
      </div>

      {/* View Box Modal */}
      {viewingBox && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0,0,0,0.5)',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          zIndex: 1000,
        }}>
          <div style={{
            backgroundColor: 'white',
            padding: '30px',
            borderRadius: '10px',
            maxWidth: '800px',
            width: '90%',
            maxHeight: '90vh',
            overflow: 'auto',
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
              <h2 style={{ margin: 0 }}>Shipping Box: {viewingBox.box_number}</h2>
              <div style={{ display: 'flex', gap: '10px' }}>
                {isBoxEditable(viewingBox) && !editingBox && (
                  <button
                    className="btn btn-primary"
                    onClick={handleEditBox}
                    style={{ padding: '8px 16px' }}
                  >
                    Edit Box
                  </button>
                )}
                <button
                  className="btn btn-secondary"
                  onClick={() => setViewingBox(null)}
                  style={{ padding: '8px 16px' }}
                >
                  Close
                </button>
              </div>
            </div>

            {message && <div className="alert alert-success">{message}</div>}
            {error && <div className="alert alert-error">{error}</div>}

            {!isBoxEditable(viewingBox) && (
              <div className="alert alert-info" style={{ marginBottom: '20px' }}>
                This box is finalized (shipping date is set). To edit, remove the shipping date first.
              </div>
            )}

            {editingBox ? (
              /* Edit Mode */
              <div style={{ marginBottom: '20px' }}>
                <div className="form-group">
                  <label>Carrier</label>
                  <select
                    value={editBoxForm.carrier}
                    onChange={(e) => setEditBoxForm({ ...editBoxForm, carrier: e.target.value })}
                  >
                    <option value="">-- Select Carrier --</option>
                    {carriers.map((carrier) => (
                      <option key={carrier.id} value={carrier.name}>
                        {carrier.name}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="form-group">
                  <label>Tracking Number</label>
                  <input
                    type="text"
                    value={editBoxForm.tracking_number}
                    onChange={(e) => setEditBoxForm({ ...editBoxForm, tracking_number: e.target.value })}
                  />
                </div>

                <div className="form-group">
                  <label>Box Size</label>
                  <select
                    value={editBoxForm.box_size}
                    onChange={(e) => setEditBoxForm({ ...editBoxForm, box_size: e.target.value })}
                  >
                    <option value="">-- Select Box Size --</option>
                    {boxSizes.map((size) => (
                      <option key={size.id} value={size.name}>
                        {size.name}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="form-group">
                  <label>Shipping Cost</label>
                  <input
                    type="number"
                    step="0.01"
                    value={editBoxForm.shipping_cost}
                    onChange={(e) => setEditBoxForm({ ...editBoxForm, shipping_cost: e.target.value })}
                  />
                </div>

                <div className="btn-group">
                  <button className="btn btn-success" onClick={handleSaveBoxEdit}>
                    Save Changes
                  </button>
                  <button className="btn btn-secondary" onClick={() => setEditingBox(false)}>
                    Cancel
                  </button>
                </div>
              </div>
            ) : (
              /* View Mode */
              <div style={{ marginBottom: '20px' }}>
                <p><strong>Carrier:</strong> {viewingBox.carrier || '-'}</p>
                <p><strong>Tracking Number:</strong> {viewingBox.tracking_number || '-'}</p>
                <p><strong>Box Size:</strong> {viewingBox.box_size || '-'}</p>
                <p><strong>Shipping Cost:</strong> {viewingBox.shipping_cost ? `$${parseFloat(viewingBox.shipping_cost).toFixed(2)}` : '-'}</p>
                <p><strong>Created:</strong> {new Date(viewingBox.created_at).toLocaleDateString()}</p>
                <p><strong>Shipping Date:</strong> {viewingBox.shipping_date ? new Date(viewingBox.shipping_date).toLocaleDateString() : 'Not set (editable)'}</p>
                <p><strong>Status:</strong> <span className={`badge badge-${viewingBox.status}`}>{viewingBox.status}</span></p>
              </div>
            )}

            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '15px' }}>
              <h3 style={{ margin: 0 }}>Items</h3>
              {isBoxEditable(viewingBox) && !showAddItem && (
                <button
                  className="btn btn-primary btn-sm"
                  onClick={() => setShowAddItem(true)}
                  style={{ padding: '6px 12px' }}
                >
                  + Add Item
                </button>
              )}
            </div>

            {/* Add Item Form */}
            {showAddItem && (
              <div style={{
                border: '1px solid #ddd',
                padding: '15px',
                borderRadius: '5px',
                marginBottom: '15px',
                backgroundColor: 'var(--color-hudson)'
              }}>
                <h4 style={{ marginTop: 0 }}>Add New Item</h4>
                <div className="form-group">
                  <label>Product (Optional for non-trackable items)</label>
                  <div style={{ position: 'relative' }} onClick={(e) => e.stopPropagation()}>
                    <input
                      type="text"
                      value={newItem.product_id ? getProductName(newItem.product_id) : newItemSearch}
                      onChange={(e) => {
                        setNewItemSearch(e.target.value);
                        setShowNewItemDropdown(true);
                        if (newItem.product_id) {
                          setNewItem({ ...newItem, product_id: '' });
                        }
                      }}
                      onFocus={() => setShowNewItemDropdown(true)}
                      placeholder="Type to search products..."
                    />
                    {newItem.product_id && (
                      <button
                        type="button"
                        onClick={() => {
                          setNewItem({ ...newItem, product_id: '' });
                          setNewItemSearch('');
                        }}
                        style={{
                          position: 'absolute',
                          right: '10px',
                          top: '50%',
                          transform: 'translateY(-50%)',
                          background: 'none',
                          border: 'none',
                          fontSize: '1.2rem',
                          cursor: 'pointer',
                          color: 'var(--color-text-muted)'
                        }}
                      >
                        ✕
                      </button>
                    )}
                    {showNewItemDropdown && !newItem.product_id && (
                      <div style={{
                        position: 'absolute',
                        top: '100%',
                        left: 0,
                        right: 0,
                        maxHeight: '200px',
                        overflowY: 'auto',
                        backgroundColor: 'white',
                        border: '1px solid #ddd',
                        borderRadius: '4px',
                        zIndex: 100,
                        boxShadow: '0 4px 6px rgba(0,0,0,0.1)'
                      }}>
                        {getFilteredProducts(newItemSearch).length === 0 ? (
                          <div style={{ padding: '10px', color: 'var(--color-text-muted)' }}>
                            No products with available inventory
                          </div>
                        ) : (
                          getFilteredProducts(newItemSearch).map(product => (
                            <div
                              key={product.id}
                              onClick={() => {
                                setNewItem({ ...newItem, product_id: product.id.toString() });
                                setNewItemSearch('');
                                setShowNewItemDropdown(false);
                              }}
                              style={{
                                padding: '10px',
                                cursor: 'pointer',
                                borderBottom: '1px solid #eee',
                                display: 'flex',
                                justifyContent: 'space-between'
                              }}
                              onMouseEnter={(e) => e.target.style.backgroundColor = '#f5f5f5'}
                              onMouseLeave={(e) => e.target.style.backgroundColor = 'white'}
                            >
                              <span>{product.name}</span>
                              <span style={{ color: 'var(--color-success)', fontWeight: 'bold' }}>
                                {product.available} avail
                              </span>
                            </div>
                          ))
                        )}
                      </div>
                    )}
                  </div>
                </div>

                <div className="form-group">
                  <label>Quantity *</label>
                  <input
                    type="number"
                    value={newItem.quantity}
                    onChange={(e) => setNewItem({ ...newItem, quantity: e.target.value })}
                    min="1"
                  />
                </div>

                <div className="form-group">
                  <label>Non-trackable Item Notes</label>
                  <textarea
                    value={newItem.non_trackable_notes}
                    onChange={(e) => setNewItem({ ...newItem, non_trackable_notes: e.target.value })}
                    placeholder="For items not in inventory"
                  />
                </div>

                <div className="btn-group">
                  <button className="btn btn-success" onClick={handleAddItemToBox}>
                    Add Item
                  </button>
                  <button className="btn btn-secondary" onClick={() => {
                    setShowAddItem(false);
                    setNewItem({ product_id: '', quantity: 1, non_trackable_notes: '' });
                    setNewItemSearch('');
                    setShowNewItemDropdown(false);
                  }}>
                    Cancel
                  </button>
                </div>
              </div>
            )}

            <table className="table">
              <thead>
                <tr>
                  <th>Product</th>
                  <th>Description</th>
                  <th>Quantity</th>
                  {isBoxEditable(viewingBox) && <th>Actions</th>}
                </tr>
              </thead>
              <tbody>
                {viewingBox.items && viewingBox.items.filter(item => item.product_id).map((item) => (
                  <tr key={item.id}>
                    <td>{item.name}</td>
                    <td>{item.description || '-'}</td>
                    <td>
                      {isBoxEditable(viewingBox) ? (
                        <input
                          type="number"
                          value={item.quantity}
                          onChange={(e) => handleUpdateItemQuantity(item.id, e.target.value)}
                          min="1"
                          style={{ width: '70px' }}
                        />
                      ) : (
                        item.quantity
                      )}
                    </td>
                    {isBoxEditable(viewingBox) && (
                      <td>
                        <button
                          className="btn btn-danger btn-sm"
                          onClick={() => handleRemoveItem(item.id)}
                          style={{ padding: '4px 8px' }}
                        >
                          Remove
                        </button>
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>

            {viewingBox.items && viewingBox.items.some(item => !item.product_id) && (
              <>
                <h3 style={{ marginTop: '20px', marginBottom: '15px' }}>Non-Trackable Items</h3>
                <table className="table">
                  <thead>
                    <tr>
                      <th>Notes</th>
                      <th>Quantity</th>
                      {isBoxEditable(viewingBox) && <th>Actions</th>}
                    </tr>
                  </thead>
                  <tbody>
                    {viewingBox.items.filter(item => !item.product_id).map((item) => (
                      <tr key={item.id}>
                        <td>{item.non_trackable_notes || 'No description'}</td>
                        <td>
                          {isBoxEditable(viewingBox) ? (
                            <input
                              type="number"
                              value={item.quantity}
                              onChange={(e) => handleUpdateItemQuantity(item.id, e.target.value)}
                              min="1"
                              style={{ width: '70px' }}
                            />
                          ) : (
                            item.quantity
                          )}
                        </td>
                        {isBoxEditable(viewingBox) && (
                          <td>
                            <button
                              className="btn btn-danger btn-sm"
                              onClick={() => handleRemoveItem(item.id)}
                              style={{ padding: '4px 8px' }}
                            >
                              Remove
                            </button>
                          </td>
                        )}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

export default Shipping;
